import {
  RETURN_ERR_MESSAGE,
  RETURN_ERR_CODE,
  ErrorContent,
  returnServiceTypes,
} from '../../constants/returns';

const {
  BACK_TO_LANDING,
  ELIGIBILITY_SUCCESS,
  HRTV_SUCCESS,
  CHECKBOX_SELECT,
  DROPDOWN_SELECT,
  BOXES_SELECT,
  SET_ERRORS,
  SSR_RESET,
} = returnServiceTypes;

export const initialState = {
  returnIds: [],
  selectedIds: [],
  eligibleIds: [],
  totalBoxes: 0,
  totalWeight: 0,
  selectedBoxes: 0,
  dfcItemCount: 0,
};

export const filterSelectedItems = (state: Object) => {
  const selectedIds = [];
  state.returnIds.forEach((id: string) => {
    if (state[id].isSelected) {
      selectedIds.push(id);
    }
  });
  return selectedIds;
};

export const returnsCheckboxSelect = (state: Object, action: Object) => {
  const { payload: itemId } = action;
  const newState = Object.assign({}, state);

  newState[itemId].isSelected = !state[itemId].isSelected;
  if (!newState[itemId].isSelected) {
    newState[itemId].qty = 0;
    newState[itemId].reason = '';
    newState[itemId].qtyError = '';
    newState[itemId].reasonError = '';
  }

  if (newState[itemId].isSelected && newState[itemId].quantityOrdered === 1) {
    newState[itemId].qty = 1;
  }

  const selectedIds = filterSelectedItems(newState);

  return {
    ...newState,
    selectedIds,
  };
};

export const getErrorMessage = (errorList: string) => {
  if (!errorList) return '';
  const errorArray = errorList.split(',');
  if (errorArray.indexOf(RETURN_ERR_CODE.WINDOW_ELAPSED_DAYS) > -1) {
    return RETURN_ERR_MESSAGE.SSR_ORDAGGR_ERR_2010;
  }
  if (errorArray.indexOf(RETURN_ERR_CODE.ERROR_PROTECTIONPLAN) > -1) {
    return RETURN_ERR_MESSAGE.SSR_ORDAGGR_ERR_2039;
  }
  return RETURN_ERR_MESSAGE.SSR_ORDAGGR_ERR_2014;
};

export const setReturnErrors = (state: Object, action: Object) => {
  const { payload } = action; // updated state with errors from validations
  return {
    ...state,
    ...payload,
  };
};

/* eslint-disable no-param-reassign */
export const returnItemsInit = (items: array) =>
  items.reduce((obj: Object, item: Object) => {
    obj[item.lineItemId] = {
      lineId: item.lineItemId,
      isSelected: false,
      qty: 0,
      qtyError: '',
      reason: '',
      reasonError: '',
      statusCodes: getErrorMessage(item.statusCodes),
      quantityOrdered: item.returnEligibilityInfo.returnableQty,
      weight: item.itemWeight,
      sku: item.sku,
      poNumber: item.poNumber,
    };
    return obj;
  }, {});
/* eslint-enable no-param-reassign */

export const getReturnIds = (returnableItems: array) =>
  returnableItems.map((item: object) => item.lineItemId);

export const returnsItemsOptionsInit = (state: Object, action: Object) => {
  const {
    payload: { response },
  } = action;
  const {
    orderLineList: { orderLine },
  } = response;
  const returnIds = getReturnIds(orderLine);

  return {
    ...state,
    ...returnItemsInit(orderLine),
    returnIds,
  };
};

const returnsDropdownSelect = (state: Object, action: Object) => {
  const {
    payload: { itemId, key, value },
  } = action;
  return {
    ...state,
    [itemId]: {
      ...state[itemId],
      [key]: value,
      [`${key}Error`]: '',
    },
  };
};

const displayProperErrorMessage = (statusCodes: string) => {
  const errorCodes = statusCodes.split(',');
  if (errorCodes.indexOf(RETURN_ERR_CODE.ERROR_HAZMAT) > -1) {
    return RETURN_ERR_MESSAGE.SSR_HRTVPOLICY_ERR_4012;
  }
  return ErrorContent.ERROR_GENERIC_ERROR;
};

/* eslint-disable no-param-reassign */
const calculateTotalBoxes = (source: Object, itemIdList: array) =>
  itemIdList.reduce((acc: Number, curr: string) => {
    acc += parseInt(source[curr].qty, 10); // set radix to base 10
    return acc;
  }, 0);

const calculateTotalWeight = (source: Object, itemIdList: array) =>
  itemIdList.reduce((acc: Number, curr: string) => {
    acc += parseInt(source[curr].qty, 10) * source[curr].weight; // set radix to base 10
    return acc;
  }, 0);

export const returnsItemsOptionsHrtv = (state: Object, action: Object) => {
  const {
    payload: { response: ssrHrtvResponse },
  } = action;

  const{
    ssrHrtvRespLineItems, et
  } = ssrHrtvResponse

  const updatedState = ssrHrtvRespLineItems.reduce(
    (newState: Object, item: Object) => {
      newState[item.lineItemId].rtvTokenId = item.tokenId;
      newState[item.lineItemId].returnDispCode = item.dispositionCd;
      newState[item.lineItemId].returnPolicyId = item.policyId;

      if (item.dispositionCd === 5 || item.dispositionCd === 8)
        newState.dfcItemCount += 1;

      if (item.statusCodes) {
        newState[item.lineItemId].hrtvError = displayProperErrorMessage(
          item.statusCodes
        );
      } else {
        newState[item.lineItemId].hrtvError = '';
        newState.eligibleIds.push(item.lineItemId);
      }

      if(et){
        newState.et = et;
      }

      return newState;
    },
    state
  );
  /* eslint-enable no-param-reassign */

  // Select boxes dropdown does not dislpay when totalBoxes is 1
  const totalBoxes = calculateTotalBoxes(
    updatedState,
    updatedState.eligibleIds
  );
  const selectedBoxes = totalBoxes === 1 ? 1 : 0;

  return {
    ...state,
    ...updatedState,
    totalBoxes,
    totalWeight: calculateTotalWeight(updatedState, updatedState.eligibleIds),
    selectedBoxes,
  };
};

export const returnsBoxesSelect = (state: Object, action: Object) => {
  const { payload } = action;
  const { value } = payload;

  return {
    ...state,
    selectedBoxes: value || 0,
  };
};

export const hrtvToLanding = (state: Object) => ({
  ...state,
  eligibleIds: [],
  totalBoxes: 0,
  selectedBoxes: 0,
  totalWeight: 0,
});

export const ssrReset = () => ({
  returnIds: [],
  selectedIds: [],
  eligibleIds: [],
  totalBoxes: 0,
  totalWeight: 0,
  selectedBoxes: 0,
  dfcItemCount: 0,
});

const ssrItemsOptions = (state: Object = initialState, action: Action) => {
  const newState = Object.assign({}, state);
  switch (action.type) {
    case SSR_RESET:
      return ssrReset();
    case BACK_TO_LANDING:
      return hrtvToLanding(newState);
    case ELIGIBILITY_SUCCESS:
      return returnsItemsOptionsInit(newState, action);
    case HRTV_SUCCESS:
      return returnsItemsOptionsHrtv(newState, action);
    case CHECKBOX_SELECT:
      return returnsCheckboxSelect(newState, action);
    case DROPDOWN_SELECT:
      return returnsDropdownSelect(newState, action);
    case BOXES_SELECT:
      return returnsBoxesSelect(newState, action);
    case SET_ERRORS:
      return setReturnErrors(newState, action);
    default:
      return state;
  }
};

export default ssrItemsOptions;
